import { Location } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  EventEmitter,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Component as BrComponent } from '@bloomreach/spa-sdk/';
import {
  AnalyticsService,
  Chip,
  DropdownComponent,
  ResponsiveService,
  SearchComponent,
} from '@frk/eds-components';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { TranslateService } from '@shared/translate/translate.service';
import {
  DropDownMultiSelect,
  DropDownMultiSelectItem,
  FiltersState,
  FilterType,
  LiteraturePageType,
  SelectedFilters,
  SelectedFiltersArray,
} from '@types';
import { Logger } from '@utils/logger';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, delay } from 'rxjs/operators';
import { LiteratureFilterStateManagerService } from '../services/literature-filter-state-manager.service';
import { NgxSpinnerService } from 'ngx-spinner';

const logger = Logger.getLogger('DocumentFiltersComponent');

@Component({
  selector: 'ft-document-filters',
  templateUrl: './document-filters.component.html',
  styleUrls: ['./document-filters.component.scss'],
})
export class DocumentFiltersComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy {
  state: FiltersState;
  selectedFilterLabels: SelectedFiltersArray[] = [];
  filterCount: number;
  stateSubscription: Subscription;
  selectedPreFilters: string;
  showResetButton: boolean;
  filterTranslationSkipList: string[] = [
    'DOCUMENT_TYPE',
    'LANGUAGE',
    'PRODUCT_TYPE',
    'SFDR',
  ];
  isMobile$: Observable<boolean>;
  isHandheld$: Observable<boolean>;
  private searchSubject: Subject<string> = new Subject();
  searchSubscription: Subscription;
  bannerTitle: string;
  public isActiveMenuLeft: boolean;

  @Input() component: BrComponent;
  @ViewChildren('filterSelection') filterSelection: QueryList<ElementRef>;
  @ViewChildren('filtersDropDowns')
  filtersDropDowns: QueryList<DropdownComponent>;
  @ViewChild('filterSearch') filterSearch: SearchComponent;
  @Input() isLoaded: boolean;
  @Input() numberOfFliterData: number;
  @Input() totalNumberOfData: number;
  @Input() pageType: LiteraturePageType;
  showDownloadExcel = false;
  @Output() downloadExcel = new EventEmitter();

  public readonly LiteraturePageType = LiteraturePageType;
  public colNumbers: { [k: string]: string | number };

  /**
   * Constructor
   * @param filterStateManagerService service
   * @param cdr cdr
   * @param translateService service
   */
  constructor(
    private filterStateManagerService: LiteratureFilterStateManagerService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private responsiveService: ResponsiveService,
    private literatureConfig: LiteratureGlobalConfigService,
    private spinner: NgxSpinnerService,
    private analyticsService: AnalyticsService
  ) {
    super();
    this.isMobile$ = this.responsiveService.isMobile$();
    this.isHandheld$ = this.responsiveService.isHandheld$();
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.colNumbers =
      this.pageType === LiteraturePageType.REGULATORY_FUND_DOCUMENTS_GLOBAL
        ? { s: 4, m: 3, l: 7 }
        : { s: 4, m: 4, l: 8 };

    this.isHandheld$.subscribe(
      (isHandheld) =>
        (this.isActiveMenuLeft =
          !isHandheld &&
          this.pageType !== LiteraturePageType.REGULATORY_FUND_DOCUMENTS_GLOBAL)
    );

    this.setBreadcrumbItems();

    this.spinner.show('documentLoadingSpinner');

    this.showDownloadExcel = this.component?.getModels()?.config?.showDownloadExcel;

    this.bannerTitle = this.component?.getModels()?.config?.litListingTitle;
    if (!this.bannerTitle || this.bannerTitle === '') {
      this.bannerTitle = this.translateService.instant(
        `literature.lit-listing-filter-browse`
      );
    }
    const urlParam = this.route?.snapshot?.queryParams?.filter;
    if (urlParam) {
      this.selectedPreFilters = urlParam;
    }
    // Get filter count for skeleton loader
    this.filterCount = this.literatureConfig.getLiteratureListingConfig()?.primaryFilters.length;

    this.stateSubscription = this.filterStateManagerService
      .getFilterState()
      .subscribe((state: FiltersState) => {
        this.state = state;
        this.mapPrimaryAndAdditionalFilterOptions();
        this.selectedFilterLabels = this.fetchSelectedFilters(this.state);
        logger.debug('state', state);
        if (this.selectedFilterLabels.length) {
          this.triggerSearchAnalyticsEvent(this.state);
        }
        this.updateUrl();
      });

    this.stateSubscription = this.filterStateManagerService
      .getFilterState()
      .pipe(delay(1000))
      .subscribe(() => {
        this.isLoaded = true;
        this.cdr.detectChanges();
      });

    this.filterStateManagerService.mapPreFilterState(this.selectedPreFilters);
    if (this.selectedPreFilters) {
      this.showResetButton = true;
    } else {
      this.showResetButton = false;
    }

    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(500))
      .subscribe((searchedValue) => {
        this.filterStateManagerService.searchByKeyword(searchedValue, true);
      });
  }
  private triggerSearchAnalyticsEvent(stateData: FiltersState): void {
    this.analyticsService.trackEvent({
      event: 'view_search_results',
      detailed_event: 'View Search Results',
      event_data: {
        facets: this.extractSelectedFilterName(),
        search_category: 'literature',
        search_num_results: this.numberOfFliterData?.toString(),
        search_term: stateData?.searchKeyword?.selected?.toString(),
      },
    });
  }

  private extractSelectedFilterName(): string {
    const filterNames: string[] = [];
    this.selectedFilterLabels?.forEach((filter) => {
      filter.selectedFilters?.forEach((data) => {
        filterNames.push(data.value);
      });
    });
    return filterNames.toString();
  }

  /**
   * Map primary & additional type filter options.
   */
  private mapPrimaryAndAdditionalFilterOptions() {
    const filters = [
      ...this.state.primaryFilters,
      ...this.state.additionalFilters,
    ];

    filters.forEach((filterType: DropDownMultiSelect) => {
      let index = 0;
      let articleIndex = null;
      if (filterType.options.length > 0) {
        filterType.options.forEach((optionProps: DropDownMultiSelectItem) => {
          if (filterType.label === 'SFDR' && optionProps.value === '6') {
            articleIndex = index;
          }
          index++;
          optionProps.label = this.getFilterOptionLabel(
            optionProps.value,
            filterType.label
          );
          optionProps.checked = filterType.selected.includes(optionProps.value)
            ? true
            : false;
        });
        if (typeof articleIndex === 'number') {
          filterType.options.splice(articleIndex, 1);
        }
      }
    });

    filters.forEach((filterType: DropDownMultiSelect) => {
      filterType.preSelectedValues = filterType.options.filter(
        (optionProps: DropDownMultiSelectItem) => optionProps.checked
      );
    });
  }

  getFilterOptionLabel(optionValue: string, filterTypeLabel: string): string {
    if (this.filterTranslationSkipList.includes(filterTypeLabel)) {
      if (filterTypeLabel === 'PRODUCT_TYPE') {
        return this.translateService.instant(
          `literature.${this.mapProductTypeFilter(optionValue)}`
        );
      }

      if (filterTypeLabel === 'SFDR') {
        return `${this.translateService.instant(
          `literature.filter-sfdr-label-article`
        )} ${optionValue}`;
      }

      return this.translateService.instant(`literature.${optionValue}`);
    }
    return optionValue;
  }
  mapProductTypeFilter(productType: string): string {
    productType = productType.toUpperCase().split(' ').join('-');
    return productType;
  }
  /**
   * Fetch the state and parse the selected filters.
   * Return the cumulative filter labels applied.
   * @param state FundFiltersState object
   */
  fetchSelectedFilters(state: FiltersState): SelectedFiltersArray[] {
    const filterLabels: SelectedFiltersArray[] = [];
    let filterTypeLables: SelectedFilters[];
    const filters: FilterType[] = [
      ...state.primaryFilters,
      ...state.additionalFilters,
    ];

    filters.forEach((filter: FilterType) => {
      if (filter.selected.length > 0) {
        filterTypeLables = [];
        filter.selected.forEach((selectedValue: string) => {
          filterTypeLables.push({
            label: this.translateService.instant('literature.' + filter.label),
            value: this.getFilterOptionLabel(selectedValue, filter.label),
          });
        });
        filterLabels.push({ selectedFilters: filterTypeLables });
      }
    });

    return filterLabels;
  }

  /**
   * Handle selected filter values.
   * Send filter label and its values to filter state service to filter out the rows.
   * @param filterClass the filter class with either of the values from 'primary'/'additional'.
   * @param selection event triggered upon value selection
   */
  updateFilter(
    filterClass: string,
    selectedLabel: string,
    selection: DropDownMultiSelectItem[]
  ): void {
    this.isLoaded = false;

    const selectedValues: string[] = [];

    if (selection.length > 0) {
      selection.forEach((item) => {
        selectedValues.push(item.value);
      });
    }

    setTimeout(() => {
      this.filterStateManagerService.updateFilter(
        filterClass,
        selectedLabel,
        selectedValues
      );
    }, 0);

    this.cdr.detectChanges();
  }

  /**
   * update browser's URL to a normalized version for current selected filters.
   */
  updateUrl() {
    const selected = this.filterStateManagerService.getSelectedFilters();
    const updateUrl = this.router
      .createUrlTree([], {
        relativeTo: this.route,
        queryParams: selected ? { filter: selected } : null,
        queryParamsHandling: 'merge',
        preserveFragment: true,
      })
      .toString();
    if (selected) {
      this.showResetButton = true;
    } else {
      this.showResetButton = false;
    }
    this.location.replaceState(updateUrl);
  }

  /**
   * Handle text input on filter search box.
   * @param event event triggered upon value entered
   */
  searchByKeyword(value: string): void {
    this.isLoaded = false;
    this.searchSubject.next(value);
  }

  /**
   * Reset the selection and hence table rows.
   */
  resetFilters(event: MouseEvent): void {
    this.isLoaded = false;

    // Prevent default behaviour
    event?.preventDefault();
    this.filterSearch.value = '';
    this.filterSearch.hasTyped = false;
    this.filterStateManagerService.resetFilters();
    this.resetDropdowns();
  }

  resetDropdowns() {
    if (this.filtersDropDowns) {
      this.filtersDropDowns.forEach((dropDown) => {
        dropDown.reset();
      });
    } else {
      logger.error('filter dropdowns: ', this.filtersDropDowns);
    }
  }

  /**
   * Remove the selected filter.
   * @param event event triggered upon removing the selected filter from chip
   */
  removeFilter(event: Chip) {
    const label = event.label;
    const value = event.value;
    let filterClass: string;
    let filterTypeToBeRemoved: FilterType;

    this.isLoaded = false;

    this.state.primaryFilters.find((filter: FilterType) => {
      const translatedLabel = this.translateService.instant(
        'literature.' + filter.label
      );
      if (translatedLabel === label) {
        filterTypeToBeRemoved = filter;
        filterClass = 'primary';
      }
    });
    this.state.additionalFilters.find((filter: FilterType) => {
      const translatedLabel = this.translateService.instant(
        'literature.' + filter.label
      );
      if (translatedLabel === label) {
        filterTypeToBeRemoved = filter;
        filterClass = 'additional';
      }
    });

    const indexOfValue = this.filterTranslationSkipList.includes(
      filterTypeToBeRemoved.label
    )
      ? filterTypeToBeRemoved.selected
          .map((translateValue) =>
            translateValue !== 'All'
              ? this.getFilterOptionLabel(
                  translateValue,
                  filterTypeToBeRemoved.label
                )
              : translateValue
          )
          .indexOf(value)
      : filterTypeToBeRemoved.selected.indexOf(value);

    if (this.filtersDropDowns) {
      const valueToBeremove = filterTypeToBeRemoved.selected[indexOfValue];
      this.filtersDropDowns.forEach((dropDown) => {
        if (dropDown.label === label) {
          dropDown.getSelected({
            value: valueToBeremove,
            label: value,
            checked: false,
          });
        }
      });
    } else {
      logger.error('filter dropdowns: ', this.filtersDropDowns);
    }
  }

  emitDownloadExcel(event): void {
    event.preventDefault();
    this.downloadExcel.emit(event);
  }

  ngOnDestroy(): void {
    this.stateSubscription?.unsubscribe();
    this.searchSubscription?.unsubscribe();
  }
}
